import React, { useState, useRef, useEffect } from 'react';

interface SliderProps {
	initialValue?: number;
	minValue?: number;
	maxValue?: number;
	onChange?: (value: number) => void;
	step?: number;
	tooltipText?: string;
}

const Slider: React.FC<SliderProps> = ({
	initialValue = 0,
	minValue = 0,
	maxValue = 1,
	onChange,
	step = 0.01,
	tooltipText,
}) => {
	const [tooltipPosition, setTooltipPosition] = useState<{ left: number }>({
		left: 0,
	});
	const [currentValue, setCurrentValue] = useState(initialValue);

	const percentage = ((currentValue - minValue) / (maxValue - minValue)) * 100;

	const sliderStyle = {
		width: '100%',
		appearance: 'none' as const,
		background: `linear-gradient(to right, rgb(91, 196, 126) 0%, rgb(91, 196, 126) ${percentage}%, rgb(239, 242, 247) ${percentage}%, rgb(239, 242, 247) 100%)`,
		borderRadius: '16pt',
		height: '0.2px',
	};

	const sliderRef = useRef<HTMLInputElement>(null);
	const thumbWidth = 35;

	useEffect(()=> {
		if(initialValue !== currentValue) setCurrentValue(initialValue);
	}, [initialValue])

	useEffect(() => {
		if (sliderRef.current) {
			const updateTooltipPosition = () => {
				const slider = sliderRef.current;
				if (!slider) return;

				const rect = slider.getBoundingClientRect();
				const trackWidth = rect.width;

				// Calculate the thumb's actual travel range
				const ratio = (currentValue - minValue) / (maxValue - minValue);
				const thumbTravelRange = trackWidth - thumbWidth;
				const thumbCenterPosition = thumbWidth / 2 + ratio * thumbTravelRange;

				setTooltipPosition({ left: thumbCenterPosition });
			};

			updateTooltipPosition();
			window.addEventListener('resize', updateTooltipPosition);

			return () => {
				window.removeEventListener('resize', updateTooltipPosition);
			};
		}
	}, [currentValue, minValue, maxValue]);

	const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = parseFloat(event.target.value);
		setCurrentValue(newValue);
		if (onChange) {
			onChange(newValue);
		}
	};

	// Render tooltip content with support for new lines
	const renderTooltipContent = () => {
		if (!tooltipText) return null;

		// Split the tooltipText by newline characters
		const lines = tooltipText.split('\n');

		return lines.map((line, index) => {
			// For each line, preserve spaces and ensure no-wrap
			// Split each line by colon to handle "Label: Value" format
			const parts = line.split(':');
			if (parts.length > 1) {
				const label = parts[0];
				const value = parts.slice(1).join(':');
				return (
					<p
						key={index}
						style={{ margin: '0', padding: '2px 0', whiteSpace: 'nowrap' }}
					>
						{label}:{value}
					</p>
				);
			}
			return (
				<p
					key={index}
					style={{ margin: '0', padding: '2px 0', whiteSpace: 'nowrap' }}
				>
					{line}
				</p>
			);
		});
	};

	return (
		<div style={{ position: 'relative', width: '100%', marginTop: '1em' }}>
			{tooltipText && (
				<div
					style={{
						position: 'absolute',
						left: `${tooltipPosition.left}px`,
						top: '-110px',
						backgroundColor: '#333',
						color: 'white',
						padding: '20px 40px',
						borderRadius: '15px',
						fontSize: '2em',
						width: 'max-content', // Ensure the tooltip width fits the content
						transform: 'translateX(-50%)',
						zIndex: 10,
						textAlign: 'center', // Center the text
					}}
				>
					{renderTooltipContent()}
					{/* Caret */}
					<div
						style={{
							position: 'absolute',
							left: '50%',
							bottom: '-6px',
							width: '0',
							height: '0',
							borderLeft: '6px solid transparent',
							borderRight: '6px solid transparent',
							borderTop: '6px solid #333',
							transform: 'translateX(-50%)',
						}}
					></div>
				</div>
			)}

			{/* Slider Input */}
			<input
				ref={sliderRef}
				type="range"
				className="custom-slider"
				min={minValue}
				max={maxValue}
				step={step}
				value={currentValue}
				onChange={handleSliderChange}
				style={sliderStyle}
			/>
		</div>
	);
};

export default Slider;
